/**
 * Export helper functions for JSON, CSV, and Excel formats
 * Adapted from CirculatorWebpage exportHelpers.ts
 */

import * as XLSX from 'xlsx';
import type { FixedRouteStop, FixedRoute, FixedRoutesState } from '../../types/fixedRoutes';
import { generateTimetable } from './timetableUtils';

/**
 * Export data structure for Fixed Routes
 */
export interface FixedRoutesExportData {
  stops: FixedRouteStop[];
  routes: FixedRoute[];
  trafficMultiplier: number;
  exportDate: string; // ISO date string
}

/**
 * Export data as JSON
 */
export function exportToJSON(state: FixedRoutesState): string {
  const exportData: FixedRoutesExportData = {
    stops: state.stops,
    routes: state.routes,
    trafficMultiplier: state.trafficMultiplier,
    exportDate: new Date().toISOString(),
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * Export timetables as CSV
 */
export function exportToCSV(routes: FixedRoute[], stops: FixedRouteStop[], trafficMultiplier: number): string {
  const stopNames = new Map(stops.map((s) => [s.id, s.name]));
  const timetables = routes.map((route) => generateTimetable(route, trafficMultiplier, stopNames));

  const headers = ['Stop Name', 'Arrival Time', 'Departure Time', 'Route Name', 'Loop Number'];
  const rows: string[][] = [headers];

  timetables.forEach((timetable) => {
    timetable.timetableEntries.forEach((entry) => {
      rows.push([
        entry.stopName,
        entry.arrivalTime,
        entry.departureTime,
        timetable.routeName,
        entry.loopNumber.toString(),
      ]);
    });
  });

  return rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
}

/**
 * Export timetables as Excel (XLSX)
 */
export function exportToExcel(routes: FixedRoute[], stops: FixedRouteStop[], trafficMultiplier: number): void {
  const workbook = XLSX.utils.book_new();
  const stopNames = new Map(stops.map((s) => [s.id, s.name]));
  const timetables = routes.map((route) => generateTimetable(route, trafficMultiplier, stopNames));

  if (timetables.length === 0) {
    // Create empty sheet
    const emptySheet = XLSX.utils.aoa_to_sheet([['No timetables available']]);
    XLSX.utils.book_append_sheet(workbook, emptySheet, 'Timetables');
  } else {
    // Track used sheet names to handle duplicates
    const usedSheetNames = new Set<string>();
    
    timetables.forEach((timetable) => {
      const data: any[][] = [
        ['Stop Name', 'Arrival Time', 'Departure Time', 'Loop Number'],
      ];

      // Group entries by loop number, then sort by arrival time within each loop
      const entriesByLoop = new Map<number, typeof timetable.timetableEntries>();
      
      timetable.timetableEntries.forEach((entry) => {
        if (!entriesByLoop.has(entry.loopNumber)) {
          entriesByLoop.set(entry.loopNumber, []);
        }
        entriesByLoop.get(entry.loopNumber)!.push(entry);
      });

      // Sort loops and entries within each loop by arrival time
      const sortedLoops = Array.from(entriesByLoop.keys()).sort((a, b) => a - b);
      
      sortedLoops.forEach((loopNumber) => {
        const loopEntries = entriesByLoop.get(loopNumber)!;
        // Sort entries within loop by arrival time
        loopEntries.sort((a, b) => {
          const [h1, m1] = a.arrivalTime.split(':').map(Number);
          const [h2, m2] = b.arrivalTime.split(':').map(Number);
          const time1 = h1 * 60 + m1;
          const time2 = h2 * 60 + m2;
          return time1 - time2;
        });

        // Add entries for this loop
        loopEntries.forEach((entry) => {
          data.push([
            entry.stopName,
            entry.arrivalTime,
            entry.departureTime,
            entry.loopNumber,
          ]);
        });
        
        // Add empty row between loops (except after the last loop)
        if (loopNumber < sortedLoops[sortedLoops.length - 1]) {
          data.push(['', '', '', '']);
        }
      });

      const sheet = XLSX.utils.aoa_to_sheet(data);
      
      // Set column widths
      sheet['!cols'] = [
        { wch: 25 }, // Stop Name
        { wch: 12 }, // Arrival Time
        { wch: 12 }, // Departure Time
        { wch: 12 }, // Loop Number
      ];

      // Clean sheet name (Excel sheet names have limitations: max 31 chars, no : \ / ? * [ ] |)
      let baseSheetName = timetable.routeName
        .replace(/[\/\\\?\*\[\]:|]/g, '_') // Replace invalid characters
        .substring(0, 31); // Excel limit is 31 characters
      
      // Handle duplicate sheet names by appending a number
      let sheetName = baseSheetName;
      let counter = 1;
      while (usedSheetNames.has(sheetName)) {
        // Truncate base name if needed to make room for counter
        const maxBaseLength = 31 - (counter.toString().length + 1); // -1 for space or underscore
        const truncatedBase = baseSheetName.substring(0, maxBaseLength);
        sheetName = `${truncatedBase}_${counter}`;
        counter++;
      }
      
      usedSheetNames.add(sheetName);
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    });
  }

  const fileName = `fixed-routes-timetable-${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}

/**
 * Export a single route's timetable as Excel (XLSX)
 */
export function exportRouteToExcel(route: FixedRoute, stops: FixedRouteStop[], trafficMultiplier: number): void {
  const workbook = XLSX.utils.book_new();
  const stopNames = new Map(stops.map((s) => [s.id, s.name]));
  const routeTrafficMultiplier = route.trafficMultiplier ?? trafficMultiplier;
  const timetable = generateTimetable(route, routeTrafficMultiplier, stopNames);

  const data: any[][] = [
    ['Stop Name', 'Arrival Time', 'Departure Time', 'Loop Number'],
  ];

  timetable.timetableEntries.forEach((entry) => {
    data.push([
      entry.stopName,
      entry.arrivalTime,
      entry.departureTime,
      entry.loopNumber,
    ]);
  });

  const sheet = XLSX.utils.aoa_to_sheet(data);
  
  // Set column widths
  sheet['!cols'] = [
    { wch: 25 }, // Stop Name
    { wch: 12 }, // Arrival Time
    { wch: 12 }, // Departure Time
    { wch: 12 }, // Loop Number
  ];

  // Clean sheet name (Excel sheet names have limitations: max 31 chars, no : \ / ? * [ ] |)
  const sheetName = route.name.replace(/[\/\\\?\*\[\]:|]/g, '_').substring(0, 31);
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName);

  const fileName = `${route.name.replace(/[\/\\\?\*\[\]:|]/g, '_')}-timetable-${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}

/**
 * Export a single route as KML
 */
export function exportRouteToKML(route: FixedRoute, stops: FixedRouteStop[]): void {
  // Helper to escape XML
  const esc = (str: string) =>
    str.replace(/[<>&'\"]/g, (c) =>
      c === '<' ? '&lt;'
      : c === '>' ? '&gt;'
      : c === '&' ? '&amp;'
      : c === '"' ? '&quot;'
      : c === "'" ? '&apos;'
      : c
    );

  const routeColor = route.color || '#FF0000';
  // Convert hex color to KML format (AABBGGRR)
  const hexToKMLColor = (hex: string): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `ff${b.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${r.toString(16).padStart(2, '0')}`;
  };

  let kml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  kml += `<kml xmlns="http://www.opengis.net/kml/2.2">\n`;
  kml += `<Document>\n`;
  kml += `<name>${esc(route.name)}</name>\n`;
  kml += `<description>Fixed Route: ${esc(route.name)}</description>\n`;

  // Style for the route line
  const styleId = `route-${route.id}`;
  kml += `<Style id="${styleId}">\n`;
  kml += `<LineStyle>\n`;
  kml += `<color>${hexToKMLColor(routeColor)}</color>\n`;
  kml += `<width>6</width>\n`;
  kml += `</LineStyle>\n`;
  kml += `</Style>\n`;

  // Get route stops in order
  const routeStops = route.stops
    .map((stopConfig) => stops.find((s) => s.id === stopConfig.stopId))
    .filter((stop): stop is FixedRouteStop => stop !== undefined && stop.coordinates !== undefined);

  // Export route line if geometry exists
  if (route.geometry?.coordinates && route.geometry.coordinates.length > 0) {
    const coordinates = route.geometry.coordinates.map(([lat, lng]) => `${lng},${lat},0`).join(' ');
    kml += `<Placemark>\n`;
    kml += `<name>${esc(route.name)} Route</name>\n`;
    kml += `<description>Route path for ${esc(route.name)}</description>\n`;
    kml += `<styleUrl>#${styleId}</styleUrl>\n`;
    kml += `<LineString>\n`;
    kml += `<coordinates>${coordinates}</coordinates>\n`;
    kml += `</LineString>\n`;
    kml += `</Placemark>\n`;
  } else if (routeStops.length >= 2) {
    // Fallback: use straight lines between stops
    const coordinates = routeStops.map((stop) => `${stop.coordinates!.lng},${stop.coordinates!.lat},0`).join(' ');
    kml += `<Placemark>\n`;
    kml += `<name>${esc(route.name)} Route</name>\n`;
    kml += `<description>Route path for ${esc(route.name)} (straight lines)</description>\n`;
    kml += `<styleUrl>#${styleId}</styleUrl>\n`;
    kml += `<LineString>\n`;
    kml += `<coordinates>${coordinates}</coordinates>\n`;
    kml += `</LineString>\n`;
    kml += `</Placemark>\n`;
  }

  // Export stops as points
  routeStops.forEach((stop, index) => {
    if (stop.coordinates) {
      const stopConfig = route.stops.find(sc => sc.stopId === stop.id);
      const waitTime = stopConfig?.waitTimeMinutes || 0;
      kml += `<Placemark>\n`;
      kml += `<name>${esc(stop.name)} (Stop ${index + 1})</name>\n`;
      kml += `<description>Stop: ${esc(stop.name)}\nWait Time: ${waitTime} minutes</description>\n`;
      kml += `<Point>\n`;
      kml += `<coordinates>${stop.coordinates.lng},${stop.coordinates.lat},0</coordinates>\n`;
      kml += `</Point>\n`;
      kml += `</Placemark>\n`;
    }
  });

  kml += `</Document>\n`;
  kml += `</kml>`;

  // Download
  const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${route.name.replace(/[\/\\\?\*\[\]]/g, '_')}-${new Date().toISOString().split('T')[0]}.kml`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Trigger file download
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  downloadBlob(blob, filename);
}

/**
 * Trigger a download for an already-built Blob (e.g. a generated GTFS zip).
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

